import { useCallback, useEffect } from 'react';
import { useSyncExternalStore } from 'react';
import { authService, authStore } from '@/services/auth.service';
import type { UserProfile } from '@/services/auth.service';

export type { UserProfile };

/**
 * Authentification — micro-API PHP pour l'identité, MySQL pour le métier.
 *
 * Ce hook n'est qu'un abonnement au magasin de `auth.service` : l'état est
 * partagé par tous les écrans, il n'y a donc pas de fournisseur React à
 * monter. Le rôle n'est jamais fourni par le client — il vient du profil, que
 * seul le serveur écrit.
 */
export function useAuth() {
  const state = useSyncExternalStore(authStore.subscribe, authStore.getState, authStore.getState);

  // Reprend la session stockée au premier montage. `bootstrap` porte son
  // propre verrou : plusieurs composants peuvent l'appeler sans boucle.
  useEffect(() => { void authService.bootstrap(); }, []);

  const signIn = useCallback((email: string, password: string) => authService.login(email, password), []);
  const signUp = useCallback(
    (email: string, password: string, fullName: string) => authService.register(email, password, fullName),
    [],
  );
  const signOut = useCallback((everywhere = false) => authService.signOut(everywhere), []);

  return {
    loading: state.loading,
    isSignedIn: state.isSignedIn,
    userId: state.user?.id ?? null,
    email: state.user?.email ?? state.profile?.email ?? '',
    profile: state.profile,
    role: state.profile?.role ?? null,

    /**
     * ⚠️ « ceo_admin » n'est plus compté comme gestionnaire de flotte.
     *
     * Le rôle interne pilote la console (admin.autolink.lu) ; il ne gère
     * aucune société et n'a pas à ouvrir les écrans flotte de l'app. Les
     * confondre faisait atterrir un compte interne sur la partie
     * gestionnaire, sans données, juste après la connexion.
     */
    isFleetManager: state.profile?.role === 'fleet_manager',
    isInternal: state.profile?.role === 'ceo_admin',

    signIn,
    signUp,
    signOut,
    refresh: authService.refreshProfile,

    /** Seule voie vers le rôle fleet_manager : créer sa société. */
    claimCompany: authService.claimCompany,

    /** Gestion du compte : mot de passe et réinitialisation. */
    changePassword: authService.changePassword,
    forgotPassword: authService.forgotPassword,
    resetPassword: authService.resetPassword,

    /** RGPD */
    exportData: authService.exportData,
    eraseAccount: authService.eraseAccount,
  };
}
